import React, { useEffect, useReducer, useState } from 'react';
import {
  useHistory, useParams, useLocation, Redirect,
} from 'react-router-dom';
import {
  Box, Container, styled, ThemeProvider,
} from '@material-ui/core';
import { usePostHog, useFeatureFlagVariantKey } from 'posthog-js/react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useQuery } from '@apollo/client';

import { isValidPhoneNumber } from 'react-phone-number-input';

import reducer, { initialState } from './preOnboardingState';
import { LanguageSelector } from '../Onboarding/components/LanguageSelector/LanguageSelector';
import {
  CheckYourEmail, SubmitEmail, ErrorState, CoverageCheckWithEmail,
} from './steps';
import Styled from './PreOnboardingPage.styles';
import { WhoAreYouEnrollingActivate } from './steps/WhoAreYouEnrollingActivate';

import { Footer } from '../Onboarding/components/Footer/Footer';

import { ConsentWidget } from '../Onboarding/components/ConsentWidget/ConsentWidget';

import { SleepioWellnessOnboardingParams } from '../SleepioWellnessOnboarding/SleepioWellnessOnboardingPage';
import CoverageRejection from '../SleepioWellnessOnboarding/steps/CoverageRejection';
import { EligibilityFormCustomization } from '../Onboarding/OnboardingFlowContext';

import SleepioLogoSrc from '@/assets/SleepioLogoIcon.svg';
import SparkDirectLogo from '@/assets/SparkDirectLogo.svg';
import theme from '@/themes/sleepioTheme';
import sparkTheme from '@/themes/sparkTheme';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { getProgram } from '@/utils/posthog';
import { EMAIL_VALIDATION_REGEX, PHONE_VALIDATION_REGEX } from '@/utils/constants';
import { isBlockedEmailDomain } from '@/utils/stringValidators';
import {
  getPdtUid, Product, isSpark as isSparkHelper,
} from '@/utils/productHelpers';
import { ELIGIBILITY_CONFIGURATION_QUERY } from '@/apollo/queries';
import Loader from '@/pages/SleepioWellnessOnboarding/components/Loader';
import { ActiveFeatureFlag } from '@/hooks/usePostHogFeatureFlags';
import {
  applyCoverageFieldsRefinement, buildEmployeeIdErrorMessage, createCoverageFieldsSchema,
} from '@/components/CoverageFields/coverageFieldsSchema';

export type PreOnboardingParams = {
  slug: string;
  product: Product;
};

const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: '100vh',
});

const emailSchema = z.string().nonempty().transform((v) => v.trim())
  .refine((v) => EMAIL_VALIDATION_REGEX.test(v), { message: 'coverage.errors.email_error' })
  .refine((v) => !isBlockedEmailDomain(v), { message: 'coverage.errors.blocked_domain_error' });

const schema = z.object({
  email: emailSchema,
  phoneNumber: z
    .union([z.literal(''), z.string().regex(PHONE_VALIDATION_REGEX)])
    .optional(),
  termsAndPrivacy: z.boolean().refine((value) => value === true),
  agreeToReceiveTextMessages: z.boolean().optional(),
  marketingConsent: z.boolean().optional(),
});

const sleepioVariantFieldsSchema = createCoverageFieldsSchema({
  firstName: 'coverage.errors.first_name_error',
  lastName: 'coverage.errors.last_name_error',
  zipCode: 'coverage.errors.zipcode_error',
  dateOfBirth: 'coverage.errors.dob_error',
  employeeId: 'coverage.errors.employee_id_error',
});

export type SetupForm = z.infer<typeof schema> & Partial<z.infer<typeof sleepioVariantFieldsSchema>>;

const PreOnboardingPage = () => {
  useAcceptedLanguage();
  useConsent();
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { slug } = useParams<SleepioWellnessOnboardingParams>();
  // It may be good to create an error state for if there is an invalid slug
  const { data, loading } = useQuery(ELIGIBILITY_CONFIGURATION_QUERY,
    { variables: { organizationSlug: slug, pdtUid: getPdtUid('sleepio') } });
  const [state, dispatch] = useReducer(reducer, initialState);
  const { step } = state;
  const { product } = useParams<PreOnboardingParams>();
  const history = useHistory();
  const location = useLocation<{ fromEmployerPath?: boolean } | undefined>();
  const params = new URLSearchParams(location.search);
  const initialAutoSelect = params.get('autoSelect');
  const isSpark = isSparkHelper(product);
  const [showWho, setShowWho] = useState(isSpark && initialAutoSelect !== 'myself');
  const eligibilityConfig = data?.eligibilityConfiguration;
  // Same field mapping SleepioWellnessOnboardingPage.tsx already uses for this exact query.
  const formCustomization: EligibilityFormCustomization = {
    showZipCode: eligibilityConfig?.collectZipcodeForEligibility,
    showEmployeeId: eligibilityConfig?.hasEmployeeId,
    showAddressField: eligibilityConfig?.hasAddressField,
    employeeIdFieldRename: eligibilityConfig?.employeeIdFieldRename,
    isGeneric: eligibilityConfig?.isGeneric,
    employeeIdValidationEnabled: eligibilityConfig?.employeeIdValidationEnabled,
    employeeIdAllowedChars: eligibilityConfig?.employeeIdAllowedChars,
    employeeIdMinLength: eligibilityConfig?.employeeIdMinLength,
    employeeIdMaxLength: eligibilityConfig?.employeeIdMaxLength,
    employeeIdValidationErrorMessage: eligibilityConfig?.employeeIdValidationErrorMessage,
    employeeIdExample: eligibilityConfig?.employeeIdExample,
  };

  // Exact match, not "not Spark" — sleepio-canopy shares the same backend PDT as sleepio.
  const isSleepioProduct = product === 'sleepio';

  useEffect(() => {
    // Skip when arriving from CheckYourOrganization: the session already started
    // there; a second reset() would fragment the funnel (PAT-1220).
    if (posthog && !location.state?.fromEmployerPath) {
      posthog.reset();
      posthog.capture('ActivationFlowStarted', { program: getProgram(product), org_slug: slug });
    }
  }, [posthog, product, getProgram, slug, location.state]);

  // False only for Sleepio, until PostHog confirms the org-tagged flags actually reloaded.
  const [flagsReady, setFlagsReady] = useState(!isSleepioProduct);

  useEffect(() => {
    // Must run after the reset() effect above, which clears the store this writes to.
    if (isSleepioProduct && posthog && slug) {
      posthog.setPersonPropertiesForFlags({ org_slug: slug }, true);
      return posthog.onFeatureFlags(() => setFlagsReady(true));
    }
    return undefined;
  }, [isSleepioProduct, posthog, slug]);

  const experimentVariant = useFeatureFlagVariantKey(
    ActiveFeatureFlag.CAP_REMOVE_EMAIL_VERIFICATION_STEP_EXPERIMENT,
  );

  const isSleepioVariant = isSleepioProduct && experimentVariant === 'variant';

  const schemaForProduct = () => {
    if (isSpark) {
      return z.object({
        email: emailSchema,
        phoneNumber: z.string().refine(
          (v) => isValidPhoneNumber(v || ''),
          { message: 'Please enter a valid phone number' },
        ),
        termsAndPrivacy: z.boolean().refine((value) => value === true),
        agreeToReceiveTextMessages: z.boolean().refine((v) => v === true),
        marketingConsent: z.boolean().optional(),
      });
    }
    if (isSleepioVariant) {
      return applyCoverageFieldsRefinement(schema.merge(sleepioVariantFieldsSchema), formCustomization, {
        zipCode: 'coverage.errors.zipcode_error',
        employeeId: buildEmployeeIdErrorMessage(formCustomization, t),
      });
    }
    return schema;
  };

  const methods = useForm<SetupForm>({
    resolver: zodResolver(schemaForProduct()),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      email: '',
      phoneNumber: '',
      termsAndPrivacy: false,
      agreeToReceiveTextMessages: false,
      marketingConsent: false,
      firstName: '',
      lastName: '',
      zipCode: '',
      employeeId: '',
      dateOfBirth: '',
    },
  });

  const renderStep = () => {
    if (isSpark && showWho) {
      return (
        <ThemeProvider theme={sparkTheme}>
          <WhoAreYouEnrollingActivate
            onMyself={() => setShowWho(false)}
            onDependent={() => {
              setShowWho(false);
              history.replace(`/coverage/${product}/${slug}?autoSelect=dependent`);
            }}
          />
        </ThemeProvider>
      );
    }
    switch (step) {
    case 'SubmitEmail':
      if (loading || !flagsReady) {
        return <Loader />;
      }
      if (isSleepioVariant) {
        return (
          <CoverageCheckWithEmail
            formCustomization={formCustomization}
            onExistingUser={() => dispatch({ type: 'SET_STEP', step: 'CheckYourEmail' })}
            onIneligible={() => dispatch({ type: 'SET_STEP', step: 'CoverageRejection' })}
            onError={() => dispatch({ type: 'SET_STEP', step: 'ErrorState' })}
          />
        );
      }
      return (
        <SubmitEmail
          onNext={() => dispatch({ type: 'SET_STEP', step: 'CheckYourEmail' })}
          onError={() => dispatch({ type: 'SET_STEP', step: 'ErrorState' })}
          isGeneric={data?.eligibilityConfiguration?.isGeneric}
        />
      );
    case 'CheckYourEmail':
      if (loading) {
        return <Loader />;
      }
      return <CheckYourEmail isGeneric={data?.eligibilityConfiguration?.isGeneric} />;
    case 'CoverageRejection':
      return <CoverageRejection />;
    case 'ErrorState':
      return <ErrorState onNext={() => dispatch({ type: 'SET_STEP', step: 'SubmitEmail' })} />;
    default:
      return null;
    }
  };

  if (isSpark && initialAutoSelect === 'myself') {
    const qs = new URLSearchParams(location.search);
    qs.delete('autoSelect');
    const clean = qs.toString();
    return (
      <Redirect
        to={{
          pathname: location.pathname,
          search: clean ? `?${clean}` : '',
          hash: location.hash,
        }}
      />
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <ContentContainer>
        <Styled.Header
          style={isSpark
            ? {
              paddingTop: 40,
              paddingBottom: 0,
            } : undefined}
        >
          <Styled.SleepioHeader
            style={isSpark
              ? {
                alignItems: 'center',
                height: 48,
              } : undefined}
          >
            {!isSpark ? (
              <img src={SleepioLogoSrc} height="48px" alt="sleepio logo" />
            ) : (
              <img
                src={SparkDirectLogo}
                alt="spark direct logo"
                height={39.5}
                width={180}
                style={{ display: 'block' }}
              />
            )}
          </Styled.SleepioHeader>
          <LanguageSelector iconOnly={isSpark} />
        </Styled.Header>
        <Container
          maxWidth={isSpark && showWho ? 'sm' : 'xs'}
          style={{ flex: 1 }}
        >
          <FormProvider {...methods}>{renderStep()}</FormProvider>
          <ConsentWidget />
        </Container>
        <Footer />
      </ContentContainer>
    </ThemeProvider>
  );
};

export default PreOnboardingPage;
